/** @format */

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IsBotService, WINDOW } from '@shared/data-access/services';

import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'homefield-product-card-color-swatches',
  templateUrl: './product-card-color-swatches.component.html',
  styleUrl: './product-card-color-swatches.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HomefieldProductCardColorSwatchesComponent implements OnInit, OnChanges {
  @Input() colors: string[] | undefined = [];
  @Input() ecode = '';
  @Input() assetSeoUrl = '';
  @Output() selectedSwatchColorChange = new EventEmitter<string>();
  @Output() extraColorsClick = new EventEmitter<void>();

  IMAGE_HOST = 'https://dks.scene7.com/is/image/dkscdn';
  maxColorSwatchesToShow = 3;
  selectedSwatchColor: string;
  moreColorsButtonAriaLabel = '';
  remainingColors = 0;
  private readonly isBotService = inject(IsBotService);
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly platformId = inject(PLATFORM_ID);
  protected window = inject(WINDOW);

  ngOnInit(): void {
    if (!this.colors?.length) {
      return;
    }

    this.selectedSwatchColor = this.colors[0];

    this.setNumberOfSwatchesToShow();
    this.setRemainingColorsAndMoreColorsAriaLabel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['colors']) {
      return;
    }

    this.setNumberOfSwatchesToShow();
    this.setRemainingColorsAndMoreColorsAriaLabel();

    const isCurrentSelectedColorInList = this.colors?.includes(this.selectedSwatchColor);
    if (!isCurrentSelectedColorInList && changes['colors'].currentValue?.length > 0) {
      this.selectedSwatchColor = changes['colors'].currentValue[0];
      this.selectedSwatchColorChange.emit(this.selectedSwatchColor);
    }
  }

  trackBySwatch(index: number, item: string): string {
    return `${index}-${item}`;
  }

  setImageColor(color: string): void {
    if (color === this.selectedSwatchColor) {
      return;
    }

    this.selectedSwatchColor = color;
    this.selectedSwatchColorChange.emit(color);
  }

  getImageSource(color: string): string {
    return `${this.IMAGE_HOST}/${this.ecode}_${color.replace(
      /[^\w]/gi,
      '_',
    )}_swatch/?$DSG_ProductSwatch$&resMode=bilin&qlt=80`;
  }

  extraColorsClicked(): void {
    this.extraColorsClick.emit();
  }

  /**
   * @description Posts to rumrunner when a missing product image is detected
   */
  handleImageError(event: { color: string; requestedImg: string }): void {
    if (
      isPlatformBrowser(this.platformId) &&
      !this.isBotService.isBot(window.navigator.userAgent)
    ) {
      this.analyticsService.emitMissingImageEvent({
        pageUrl: window.location.href,
        requestedImg: event.requestedImg,
        ecode: this.ecode,
        color: event.color,
      });
    }
  }

  private setNumberOfSwatchesToShow(): void {
    if (!isPlatformBrowser(this.platformId) || !this.colors?.length) {
      return;
    }

    const widthQuery = matchMedia('(max-width: 1531px)');

    // Initial check of the media query
    this.maxColorSwatchesToShow = widthQuery.matches ? 3 : 6;

    // Check when window width passes the media query
    widthQuery.onchange = (event) => {
      this.maxColorSwatchesToShow = event.matches ? 3 : 6;
      this.setRemainingColorsAndMoreColorsAriaLabel();
      this.cdr.detectChanges();
    };
  }

  private setRemainingColorsAndMoreColorsAriaLabel(): void {
    this.remainingColors = this.colors.length - this.maxColorSwatchesToShow;
    this.moreColorsButtonAriaLabel = `Show ${this.remainingColors} more color option${this.remainingColors > 1 ? 's' : ''}.`;
  }
}
